import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import * as allConstants from '../../constants/allConstants';
import * as Yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {FormProvider, useForm} from 'react-hook-form';
import CustomFormBlock from '../../pieces/inputs/formBlock';
import {Box, Grid} from '@mui/material';
import FormSubmitButtons from '../../pieces/formSubmitButtons';
import validator from '../../utils/validators/validator';
import {inputTypes} from '../../pieces/inputs/formInput';
import ApiUtils from '../../utils/apiUtils';
import base_url from '../../baseUrls';
import Utils from '../../utils/utils';
import clsx from 'clsx';
import {globalUseClasses} from '../../styles/globalClasses';
import {toast} from 'react-toastify';
import axios from 'axios';

const TAG_FIELDS = allConstants.TAG_FIELDS;

const TagJobForm = (props)=> {
  
  const classes = {...globalUseClasses()};
  const [canSubmit, setCanSubmit] = useState(true);
  /**
   * Companies this user may own a tag in, resolved server-side and passed down by the view,
   * so the picker matches exactly what the API will accept. Own company comes first, flagged.
   */
  const tagCompanies = props.tagCompanies;
  const isParentCompany = tagCompanies.length > 1;
  const ownCompanyId = useMemo(
    () => tagCompanies.find((c) => c.isOwnCompany)?.companyId ?? '',
    [tagCompanies]
  );
  // Marked so "my company" is never confused with a sub-account in the list.
  const companyOptions = useMemo(
    () => tagCompanies.map((c) => ({
      name: c.isOwnCompany ? `${c.companyName} (my company)` : c.companyName,
      value: c.companyId,
    })),
    [tagCompanies]
  );
  const defaultCompanyId = props?.tagData?.[TAG_FIELDS.COMPANY_ID.api_name] || ownCompanyId;

  /**
   * Tag names are unique per company, so the check has to name the company the tag will
   * end up in -- for a prime admin that may be a sub-account rather than their own company.
   *
   * A failed request is treated as "allow": the server checks uniqueness again on submit
   * and returns TAG_NOT_UNIQUE, so a blip degrades to a submit-time error rather than
   * blocking a valid name. It is logged rather than swallowed silently.
   */
  const tagNameIsUnique = async (tagName, companyId)=> {
    if(!tagName) return false;
    try{
      const authToken = await ApiUtils.getAccessToken();
      let url = `${base_url.api}tagJobs/tag-is-unique?tagName=${encodeURIComponent(tagName)}`;
      const id = props?.tagData?.[TAG_FIELDS.ID.api_name];
      if(id) {
        url += `&id=${id}`;
      }
      if (companyId) {
        url += `&companyId=${companyId}`;
      }
      const response = await axios.get(url, {
        headers: {Authorization: authToken}});
      if(response?.data?.data) {
        return !!response.data.data.isUnique;
      }
      return true;
    } catch (e) {
      console.warn('Tag uniqueness check failed, deferring to the server', e);
      return true;
    }
  };

  const validationSchemaAdd = Yup.object().shape({
    [TAG_FIELDS.TAG_NAME.api_name]: Yup.string()
      .required(allConstants.ERROR_MSG.REQUIRED_FIELD).nullable()
      .test({
        test: value => validator.isValidTagName(value),
        message: allConstants.ERROR_MSG.SHOULD_NOT_CONTAIN(['&'])
      })
      .test({
        name: 'tagNameIsUnique',
        message: allConstants.ERROR_MSG.SHOULD_BE_UNIQUE(TAG_FIELDS.TAG_NAME.label),
        // Regular function so `this.parent` can read the sibling company selection.
        test: function (value) {
          const companyId = this.parent?.[TAG_FIELDS.COMPANY_ID.api_name] || defaultCompanyId;
          return tagNameIsUnique(value, companyId);
        },
      }),
    // Declared so the value survives validation and `this.parent` above can read it.
    [TAG_FIELDS.COMPANY_ID.api_name]: Yup.string().nullable(),
  });

  const formOptions = {
    resolver: yupResolver(validationSchemaAdd),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    criteriaMode:'all',
    defaultValues: {
      [TAG_FIELDS.TAG_NAME.api_name]: props?.tagData?.[TAG_FIELDS.TAG_NAME.api_name] || '',
      [TAG_FIELDS.COMPANY_ID.api_name]: defaultCompanyId,
    }
  };
  const formMethods = useForm(formOptions);
  if(Utils.isDevEnv()) {
    window.tagForm = formMethods;
  }

  useEffect(() => {
    if (!defaultCompanyId) return;
    if (formMethods.getValues()?.[TAG_FIELDS.COMPANY_ID.api_name]) return;
    // useForm captured defaultValues before the company scope had loaded.
    formMethods.setValue(TAG_FIELDS.COMPANY_ID.api_name, defaultCompanyId);
  }, [defaultCompanyId]);

  const submitActions = {
    edit: {done: 'edited', action: 'edit'},
    create: {done: 'created', action: 'create'},
  };

  const handleSubmitResponse = (result, actionType)=> {
    if(result.status === 'success') {
      if(props?.closeModal) {
        props.closeModal();
      }
      // Name the owning company back to the user: a prime admin can pick a sub-account,
      // and the tag is only usable on that company's jobs. Prefer what the server saved.
      const savedCompanyName = result?.data?.tag?.[TAG_FIELDS.COMPANY_NAME.api_name]
        || tagCompanies.find((c) => c.companyId === formMethods.getValues()?.[TAG_FIELDS.COMPANY_ID.api_name])?.companyName;
      const forCompany = isParentCompany && savedCompanyName ? ` for ${savedCompanyName}` : '';
      toast.success(`Tag successfully ${submitActions?.[actionType]?.done}${forCompany}. Please refresh page to see changes.`, {
        toastId: `success_${actionType}_tag`,
        autoClose: 1000,
        onClose: () => {
          try{
            if(props.successAction) {
              props.successAction();
            }
          } catch (e){}
        }
      });
    } else{
      toast.error(`Error on tag ${submitActions[actionType].create}. ${result?.message ? `Message: ${result.message}.` : ''}`, {
        toastId: `error_${submitActions[actionType].create}_tag`,
      });
    }
  };

  const validateEditUniqueFields = async (value, fieldName)=> {
    if(fieldName === TAG_FIELDS.TAG_NAME.api_name) {
      const companyId = formMethods.getValues()?.[TAG_FIELDS.COMPANY_ID.api_name] || defaultCompanyId;
      return tagNameIsUnique(value, companyId);
    }
    return true;
  };

  const onSubmit = async ()=> {
    const data = formMethods.getValues();
    const authToken = await ApiUtils.getAccessToken();
    if(!authToken) {
      return;
    }

    if(props.editMode) {
      const dirtyFields = formMethods.formState.touchedFields;
      const cleanData = Utils.dirtyValues(dirtyFields, data);
      cleanData.id = props.tagData[TAG_FIELDS.ID.api_name];
      let validatePromises = [];
      Object.entries(cleanData).forEach(([key, value]) => {
        validatePromises.push(validateEditUniqueFields(value, key).then((isValid)=> {
          if(!isValid) {
            formMethods.setError(key, {message: allConstants.ERROR_MSG.SHOULD_BE_UNIQUE(key)});
          }
        }));
      });

      Promise.all(validatePromises).then(async ()=> {
        if(Object.keys(formMethods.formState.errors).length === 0) {
          const response = await fetch(
            `${base_url.api}tagJobs/edit`,
            {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: authToken,
              },
              body: JSON.stringify(
                cleanData,
              ),
            }
          );
          let submitted = await response.json();
          handleSubmitResponse(submitted, submitActions.edit.action);
        }
      });

    } else {
      const response = await fetch(
        `${base_url.api}tagJobs/create`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify(
            data,
          ),
        }
      );
      let submitted = await response.json();
      handleSubmitResponse(submitted, submitActions.create.action);
    }
  };


  const onCancelAdd = (e)=> {
    e.preventDefault();
    formMethods.reset();
    props.closeModal();
  };
  const formInputDefaultProps = {
    viewMode: false,
    renderLabel: true,
  };

  return(
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}  method="POST">
          <Grid container xs={12}>
            <Box item xs={12} sm={12} className={clsx(classes.general_col)} sx={{
              width: '100%',
              '.MuiButtonBase-root-MuiMenuItem-root': {
                background: 'red',
                textTransform:'uppercase !important'
              }
            }}>
              {/* Only a prime company has sub-accounts to choose between. */}
              {isParentCompany && <CustomFormBlock
                {...formInputDefaultProps}
                inputType={inputTypes.singleSelect}
                options={companyOptions}
                field={{...TAG_FIELDS.COMPANY_ID, required: true, label: 'Company this tag belongs to'}}
                defaultValue={defaultCompanyId}
                fullWidth
                isEditable={true}
                allowNone={false}
              />}
              <CustomFormBlock
                {...formInputDefaultProps}
                field={TAG_FIELDS.TAG_NAME}
                defaultValue={props?.tagData?.[TAG_FIELDS.TAG_NAME.api_name]}
                fullWidth
                isEditable={true}
                preventCapitalize={true}
              />
            </Box>
            <Grid xs={12}>
              <Box display={'flex'} justifyContent={'center'}>
                <FormSubmitButtons cancelAction={onCancelAdd} submitText={'Save'} cancelText={'Cancel'}
                  disableSubmit={!canSubmit}
                />
              </Box>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </>
  );
};
TagJobForm.propTypes = {
  closeModal: PropTypes.func.isRequired,
  tagData: PropTypes.shape({
    [TAG_FIELDS.ID.api_name]:PropTypes.string.isRequired,
    [TAG_FIELDS.TAG_NAME.api_name]:PropTypes.string,
  }),
  editMode: PropTypes.bool,
  successAction: PropTypes.func,
  tagCompanies: PropTypes.arrayOf(PropTypes.shape({
    companyId: PropTypes.string,
    companyName: PropTypes.string,
    isOwnCompany: PropTypes.bool,
  })),
};

TagJobForm.defaultProps = {
  tagCompanies: [],
  tagData: {
    [TAG_FIELDS.TAG_NAME.api_name]:undefined,
    successAction: ()=>{}
  },
  editMode: false
};
export default TagJobForm;
